import { useEffect, useState } from "react"
import {
	Button,
	Text,
	Stack,
	Checkbox,
	Group,
	Image,
	Modal,
	Title
} from "@mantine/core"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { stylizeText } from "@utils/stylizeText"
import cPLogo from "@assets/svg/cp-logo.svg"
import styles from "./legalAcceptance.module.css"
import { useIsMobile } from "@utils/device.utils"
import { useLegalAcceptance } from "./hooks/useLegalAcceptance"
import { eventTrackingItems } from "@utils/eventTracking"

interface LegalAcceptanceProps {
	isOpen: boolean
	onClose: () => void
	termsContent?: string
}

export const LegalAcceptance = ({ isOpen }: LegalAcceptanceProps) => {
	const { dictionary: registrationDictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { dictionary: applicationDictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.APPLICATION
	)
	const {
		needsTravelAgentGuidelines,
		needsTermsOfUse,
		handleAcceptTerms,
		isAcceptingTerms
	} = useLegalAcceptance()
	const [acceptTermsConditions, setAcceptTermsConditions] = useState(false)
	const [acceptTravelAgentGuideliness, setAcceptTravelAgentGuideliness] =
		useState(false)

	const onLinkClick = (url: string) => {
		window.open(window.location.origin + url, "_blank")
	}
	const isMobile = useIsMobile()

	useEffect(() => {
		eventTrackingItems({
			event_name: "popup_impression",
			modal_name: applicationDictionary?.policiesTitle
		})
	}, [])

	const handleOnTerms = () => {
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "click",
			interactionType: "agree_continue_click",
			navLinkText: applicationDictionary?.agreeAndContinueButtonLabel ?? ""
		})
	}

	return (
		<Modal.Root
			opened={isOpen}
			onClose={() => {}}
			centered={true}
			size={780}
			zIndex={9999}
		>
			<Modal.Overlay opacity={0.92} bg="var(--mantine-color-royal-5)" />
			<Modal.Content>
				<Modal.Header p={0} w={"100%"}>
					<Stack
						align="center"
						p={"16px"}
						w={"100%"}
						className={styles.modalHeader}
					>
						<Image
							src={cPLogo}
							alt="Company Logo"
							w={{ base: 199, sm: 214 }}
							h={{ base: 28, sm: 31 }}
						/>
					</Stack>
				</Modal.Header>

				<Modal.Body p={0}>
					<Stack w={"100%"} gap={0} maw={780} p={"40px 45px"}>
						<Title fz={{ base: 28, sm: 32 }} fw={600} mb={16} ta="center">
							{applicationDictionary?.policiesTitle ?? ""}
						</Title>
						<Text fz={{ base: 14, sm: 16 }} ta="center" mb={40}>
							{applicationDictionary?.policiesDescription ?? ""}
						</Text>

						<Stack gap={isMobile ? 18 : 24} align="center">
							{needsTermsOfUse && (
								<Group w={"100%"} maw={480} gap={12}>
									<Checkbox
										checked={acceptTermsConditions}
										onChange={(event) =>
											setAcceptTermsConditions(event.currentTarget.checked)
										}
									/>
									<Text flex={1} fz={14} fw={400} lh={1.5}>
										{stylizeText(
											registrationDictionary?.agreeLinkLabel1 ?? "",
											{
												fz: 14,
												fwa: 600,
												onAnchorClick: () =>
													onLinkClick(
														registrationDictionary?.agreeLinklabelUrl1 ?? ""
													)
											}
										)}
									</Text>
								</Group>
							)}
							{needsTravelAgentGuidelines && (
								<Group w={"100%"} maw={480} gap={12}>
									<Checkbox
										checked={acceptTravelAgentGuideliness}
										onChange={(event) =>
											setAcceptTravelAgentGuideliness(
												event.currentTarget.checked
											)
										}
									/>
									<Text flex={1} fz={14} fw={400} lh={1.5}>
										{stylizeText(
											registrationDictionary?.agreeLinkLabel2 ?? "",
											{
												fz: 14,
												fwa: 600,
												onAnchorClick: () =>
													onLinkClick(
														registrationDictionary?.agreeLinkLabelUrl2 ?? ""
													)
											}
										)}
									</Text>
								</Group>
							)}
						</Stack>
					</Stack>
					<Stack
						className={styles.modalFooter}
						align="center"
						p={"24px"}
						w={"100%"}
					>
						<Button
							onClick={() => {
								handleAcceptTerms?.()
								handleOnTerms()
							}}
							disabled={
								(needsTermsOfUse && !acceptTermsConditions) ||
								(needsTravelAgentGuidelines && !acceptTravelAgentGuideliness)
							}
							size="lg"
							variant="dark-blue"
							loading={isAcceptingTerms}
						>
							{applicationDictionary?.agreeAndContinueButtonLabel ?? ""}
						</Button>
					</Stack>
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	)
}
